import { useState, useEffect, useCallback } from 'react';

interface UseArrowNavigationParams {
  containerRef: React.RefObject<HTMLElement>;
  onScroll?: () => void;
}

interface UseArrowNavigationReturn {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
}

const SCROLL_THRESHOLD = 1; // px tolerance for floating point

const getCardScrollAmount = (): number => {
  if (typeof window === 'undefined') return 256; // SSR fallback
  const width = window.innerWidth;
  if (width >= 768) return 320 + 16; // md: desktop (card + gap)
  if (width >= 640) return 280 + 16; // sm: tablet (card + gap)
  return 240 + 16; // mobile (card + gap)
};

const getPrefersReducedMotion = (): boolean => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

export const useArrowNavigation = ({
  containerRef,
  onScroll,
}: UseArrowNavigationParams): UseArrowNavigationReturn => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrowVisibility = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD);
  }, [containerRef]);

  const scrollLeft = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = getCardScrollAmount();

    container.scrollBy({
      left: -scrollAmount,
      behavior: getPrefersReducedMotion() ? 'auto' : 'smooth',
    });

    onScroll?.();
  }, [containerRef, onScroll]);

  const scrollRight = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = getCardScrollAmount();

    container.scrollBy({
      left: scrollAmount,
      behavior: getPrefersReducedMotion() ? 'auto' : 'smooth',
    });

    onScroll?.();
  }, [containerRef, onScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateArrowVisibility();

    container.addEventListener('scroll', updateArrowVisibility);

    // Add guard for ResizeObserver
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateArrowVisibility);
      resizeObserver.observe(container);
    }

    return () => {
      container.removeEventListener('scroll', updateArrowVisibility);
      resizeObserver?.disconnect();
    };
  }, [containerRef, updateArrowVisibility]);

  return {
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  };
};
